import React from 'react';
import ReactLoading from 'react-loading';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Row, Col } from 'react-bootstrap';
import { Select } from '../components/fields';
import { DiscountCard, PromoCard } from '../components/cards';
import { SaveDiscountModal, SavePromoModal } from '../components/modals';
import { Confirm, Toast } from '../components/alerts';
import { DiscountService, PromoService } from '../services';
import { LocalStorage } from '../utils';

import '../styles/discount.css';
import '../styles/promo.css';

class DiscountPage extends React.Component {
  constructor(props) {
    super(props);

    const filter = LocalStorage.getFilter();

    this.state = {
      loading: false,
      venue: filter && filter.venue ? filter.venue : null,
      discounts: [],
      promos: [],
      activeTab: 'DISCOUNTS',
    };

    this.fetchData = this.fetchData.bind(this);
    this.fetchDiscounts = this.fetchDiscounts.bind(this);
    this.fetchPromos = this.fetchPromos.bind(this);
    this.addDiscountBtnClick = this.addDiscountBtnClick.bind(this);
    this.addPromoBtnClick = this.addPromoBtnClick.bind(this);
    this.onVenueSelectChange = this.onVenueSelectChange.bind(this);
    this.onAddDiscountComplete = this.onAddDiscountComplete.bind(this);
    this.onSavePromoComplete = this.onSavePromoComplete.bind(this);
    this.onDiscountActionChange = this.onDiscountActionChange.bind(this);
    this.onPromoActionChange = this.onPromoActionChange.bind(this);

    this.discountService = new DiscountService();
    this.promoService = new PromoService();
  }

  componentDidMount() {
    this.fetchData();
  }

  async fetchData() {
    this.setState({ loading: true });

    const venue = this.state.venue;

    const discountResult = await this.discountService.get({
      venue_id: venue ? venue._id : undefined,
    });

    let selectedVenue = venue;
    let discounts = [];

    if (discountResult && discountResult.success) {
      discounts = discountResult.discounts || [];
      selectedVenue = discountResult.selected_venue || selectedVenue;
    }

    const promoResult = await this.promoService.get({
      venue_id: selectedVenue ? selectedVenue._id : undefined,
    });

    let promos = [];

    if (promoResult && promoResult.success) {
      promos = promoResult.promos || [];
      selectedVenue = promoResult.selected_venue || selectedVenue;
    }

    this.setState({
      discounts,
      promos,
      venue: selectedVenue,
      loading: false,
    });

    if (this.venueSelect && selectedVenue) {
      this.venueSelect.setValue(selectedVenue.name);
    }
  }

  async fetchDiscounts() {
    const venue = this.state.venue;

    const result = await this.discountService.get({
      venue_id: venue ? venue._id : undefined,
    });

    this.setState({
      discounts:
        result && result.success ? result.discounts || [] : [],
      loading: false,
    });
  }

  async fetchPromos() {
    const venue = this.state.venue;

    const result = await this.promoService.get({
      venue_id: venue ? venue._id : undefined,
    });

    this.setState({
      promos: result && result.success ? result.promos || [] : [],
      loading: false,
    });
  }

  addDiscountBtnClick() {
    const { venue } = this.state;
    const { t } = this.props;

    if (venue) {
      this.saveDiscountModal.show(true, venue);
    } else {
      Toast.warning(t('Please select a venue from filter!'));
    }
  }

  addPromoBtnClick() {
    const { venue } = this.state;
    const { t } = this.props;

    if (venue) {
      this.savePromoModal.show(true, venue);
    } else {
      Toast.warning(t('Please select a venue from filter!'));
    }
  }

  onVenueSelectChange(venue) {
    this.setState({ venue }, this.fetchData);
  }

  onAddDiscountComplete() {
    this.setState({ loading: true }, this.fetchDiscounts);
  }

  onSavePromoComplete() {
    this.setState({ loading: true }, this.fetchPromos);
  }

  async onDiscountActionChange(data, action) {
    const { t } = this.props;
    let result = null;

    switch (action.label) {
      case 'EDIT':
        this.saveDiscountModal.show(true);
        this.saveDiscountModal.setFields(data);
        break;

      case 'DELETE': {
        const confirm = await Confirm.open(
          t('Discount Deletion'),
          t('Are you sure to delete?'),
          t('Yes'),
          t('No')
        );

        if (confirm) {
          this.setState({ loading: true });
          result = await this.discountService.delete(data._id);
        }
        break;
      }

      default:
        break;
    }

    if (result && result.success) {
      await this.fetchDiscounts();
    } else if (result) {
      this.setState({ loading: false });
    }
  }

  async onPromoActionChange(data, action) {
    const { t } = this.props;
    let result = null;

    switch (action.label) {
      case 'EDIT':
        this.savePromoModal.show(true, this.state.venue);
        this.savePromoModal.setFields(data, this.state.venue);
        break;

      case 'DELETE': {
        const confirm = await Confirm.open(
          t('Promotion Deletion'),
          t('Are you sure to delete?'),
          t('Yes'),
          t('No')
        );

        if (confirm) {
          this.setState({ loading: true });

          result = await this.promoService.delete(
            data._id,
            this.state.venue._id
          );
        }
        break;
      }

      default:
        break;
    }

    if (result && result.success) {
      await this.fetchPromos();
    } else if (result) {
      this.setState({ loading: false });
    }
  }

  render() {
    const {
      loading,
      discounts,
      promos,
      activeTab,
    } = this.state;

    const { t, me } = this.props;
    const canEdit = me.role === 'ADMIN' || me.role === 'MANAGER';

    return (
      <div className='content'>
        <SaveDiscountModal
          onRef={(ref) => (this.saveDiscountModal = ref)}
          onComplete={this.onAddDiscountComplete}
        />

        <SavePromoModal
          onRef={(ref) => (this.savePromoModal = ref)}
          onComplete={this.onSavePromoComplete}
        />

        <div className='content-title'>
          <div className='content-title-label'>
            {t('Discounts & Promotions')}
          </div>

          <div className='content-title-buttons'>
            {canEdit && activeTab === 'DISCOUNTS' && (
              <button
                className='content-title-button'
                onClick={this.addDiscountBtnClick}
              >
                <FontAwesomeIcon icon={faPlus} /> {t('ADD DISCOUNT')}
              </button>
            )}

            {canEdit && activeTab === 'PROMOTIONS' && (
              <button
                className='content-title-button'
                onClick={this.addPromoBtnClick}
              >
                <FontAwesomeIcon icon={faPlus} /> {t('ADD PROMOTION')}
              </button>
            )}
          </div>
        </div>

        <div className='content-body'>
          <div className='content-header'>
            <Row>
              <Col md={12}>
                <p className='discount-container-title'>{t('Filters')}</p>
                <div className='discount-spacer' />
              </Col>

              <Col md={3}>
                <Select
                  fullWidth
                  type='venues'
                  field='name'
                  size='sm'
                  label={t('Venue')}
                  onChange={this.onVenueSelectChange}
                  onRef={(ref) => (this.venueSelect = ref)}
                />
              </Col>
            </Row>
          </div>

          <div
            style={{
              display: 'flex',
              marginBottom: '24px',
              borderBottom: '1px solid #ddd',
            }}
          >
            <button
              type='button'
              onClick={() => this.setState({ activeTab: 'DISCOUNTS' })}
              style={{
                border: 'none',
                background: 'transparent',
                padding: '14px 20px',
                cursor: 'pointer',
                fontWeight:
                  activeTab === 'DISCOUNTS' ? 'bold' : 'normal',
                borderBottom:
                  activeTab === 'DISCOUNTS'
                    ? '3px solid currentColor'
                    : '3px solid transparent',
              }}
            >
              {t('DISCOUNTS')}
            </button>

            <button
              type='button'
              onClick={() => this.setState({ activeTab: 'PROMOTIONS' })}
              style={{
                border: 'none',
                background: 'transparent',
                padding: '14px 20px',
                cursor: 'pointer',
                fontWeight:
                  activeTab === 'PROMOTIONS' ? 'bold' : 'normal',
                borderBottom:
                  activeTab === 'PROMOTIONS'
                    ? '3px solid currentColor'
                    : '3px solid transparent',
              }}
            >
              {t('PROMOTIONS')}
            </button>
          </div>

          {loading ? (
            <div className='margin-auto'>
              <ReactLoading
                type='spin'
                color='#000'
                height='24px'
                width='24px'
              />
            </div>
          ) : activeTab === 'DISCOUNTS' ? (
            <Row>
              <Col md={12}>
                <div className='discount-container'>
                  <p className='discount-container-title'>
                    {t('Discounts')}
                  </p>

                  <div className='discount-spacer' />

                  <div className='discount-container-inline'>
                    {discounts && discounts.length > 0 ? (
                      discounts.map((discount, index) => (
                        <DiscountCard
                          key={index}
                          discount={discount}
                          data={discount}
                          actions={[
                            { label: 'EDIT', title: t('EDIT') },
                            {
                              label: 'DELETE',
                              title: t('DELETE'),
                              red: true,
                            },
                          ]}
                          onChange={this.onDiscountActionChange}
                        />
                      ))
                    ) : (
                      <p className='discount-no-content'>
                        {t('No Data Found')}
                      </p>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md={12}>
                <div className='promo-container'>
                  <p className='promo-container-title'>
                    {t('Promotions')}
                  </p>

                  <div className='promo-spacer' />

                  <div className='promo-container-inline'>
                    {promos && promos.length > 0 ? (
                      promos.map((promo) => (
                        <PromoCard
                          key={promo._id}
                          promo={promo}
                          data={promo}
                          actions={[
                            { label: 'EDIT', title: t('EDIT') },
                            {
                              label: 'DELETE',
                              title: t('DELETE'),
                              red: true,
                            },
                          ]}
                          onChange={this.onPromoActionChange}
                        />
                      ))
                    ) : (
                      <p className='promo-no-content'>
                        {t('No Data Found')}
                      </p>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    );
  }
}

export default withTranslation()(withRouter(DiscountPage));
